// src/data/books/FundamentosBlockchain/data/cap7.js
const BLOCKCHAIN_FUNDAMENTOS_CH07 = {
  id: "blockchain-fundamentos-7",
  slug: "arquitectura-distribuida",
  contentId: 7,
  title: "Arquitectura Distribuida y Modelos de Almacenamiento en Blockchain",
  sections: [
    {
      id: "introduccion",
      heading: "Arquitectura Distribuida: el corazón de la resiliencia digital",
      text: `En el núcleo de la tecnología blockchain se encuentra un principio esencial: 
la eliminación del control centralizado sobre la información. 
A diferencia de los sistemas tradicionales, donde un único servidor gestiona, almacena y distribuye los datos, 
en una arquitectura distribuida cada nodo de la red posee una copia idéntica del registro. 
Esto garantiza integridad, seguridad y disponibilidad constante, aun si partes de la red fallan o son comprometidas.

La arquitectura distribuida no solo representa una evolución técnica, sino también filosófica: 
traslada la confianza de una autoridad central hacia un consenso colectivo validado criptográficamente. 
En este modelo, la información no depende de una sola entidad, sino de una red global de computadoras que trabajan de forma coordinada.`
    },
    {
      id: "comparacion-sistemas",
      heading: "De la arquitectura centralizada a la distribuida",
      text: `En un sistema centralizado tradicional, toda la información se almacena en un servidor principal o en una base de datos única. 
Los usuarios deben confiar en la entidad que administra el servidor para mantener la disponibilidad y seguridad de los datos. 
Si ese servidor se desconecta o es atacado, todo el sistema queda inoperante.

Por el contrario, en blockchain y otras arquitecturas distribuidas, la base de datos se replica en múltiples nodos. 
Cada uno conserva una copia sincronizada del registro, actualizada mediante mecanismos de consenso. 
Esto implica que ningún participante tiene poder absoluto sobre los datos y que la red puede seguir operando incluso si algunos nodos fallan. 
A medida que el número de nodos crece, la red se vuelve más robusta, segura y difícil de manipular.`
    },
    {
      id: "funcionamiento",
      heading: "Cómo funciona el almacenamiento distribuido en blockchain",
      text: `Cada nodo de la red blockchain mantiene una réplica exacta de la cadena de bloques. 
Cuando se genera una nueva transacción, esta se transmite a todos los nodos, que la verifican y la incorporan en un nuevo bloque mediante un consenso común (Proof of Work, Proof of Stake, o variantes más modernas). 
Una vez que el bloque se valida y se añade a la cadena, la actualización se replica en toda la red.

Si un nodo intenta modificar un bloque de forma fraudulenta, los demás detectan la discrepancia comparando sus copias. 
El sistema descarta automáticamente la versión incorrecta y conserva la que coincide con la mayoría. 
De este modo, la arquitectura distribuida proporciona una resistencia natural a la manipulación y garantiza la inmutabilidad de la información.`
    },
    {
      id: "ventajas",
      heading: "Ventajas de una arquitectura distribuida",
      text: `Las arquitecturas distribuidas presentan múltiples beneficios que las hacen ideales para entornos modernos de datos interconectados:
      
1. **Alta disponibilidad:** la red continúa operando aunque varios nodos estén desconectados.  
2. **Resiliencia ante fallos:** los datos pueden recuperarse desde cualquier otro nodo activo.  
3. **Tolerancia a ataques:** es prácticamente imposible comprometer simultáneamente todas las copias del registro.  
4. **Integridad verificable:** cada bloque y transacción están firmados digitalmente, garantizando autenticidad.  
5. **Escalabilidad horizontal:** se pueden agregar más nodos sin rediseñar la infraestructura.  
6. **Confianza descentralizada:** no se requiere una autoridad central, sino que la verificación se basa en criptografía y consenso.`
    },
    {
      id: "desventajas",
      heading: "Desafíos y consideraciones técnicas",
      text: `Pese a sus ventajas, las arquitecturas distribuidas también presentan desafíos significativos:
      
- **Complejidad técnica:** requieren mayor coordinación entre nodos y sincronización precisa.  
- **Costos de energía:** en algunos modelos (como Proof of Work), el consumo energético puede ser elevado.  
- **Rendimiento variable:** las operaciones pueden ser más lentas que en sistemas centralizados, especialmente en redes públicas.  
- **Gestión de versiones:** mantener la coherencia entre miles de copias distribuidas exige protocolos de consenso robustos.  
- **Privacidad y cumplimiento:** equilibrar transparencia con confidencialidad es uno de los retos más importantes de 2025.`
    },
    {
      id: "actualizacion-2025",
      heading: "Evolución de las arquitecturas distribuidas al 2025",
      text: `A lo largo de la última década, la arquitectura distribuida ha evolucionado hacia modelos híbridos que combinan lo mejor de ambos mundos: 
la eficiencia de las bases centralizadas y la resiliencia de las distribuidas. 
En 2025, las redes blockchain de nueva generación (como Ethereum 2.0, Polkadot, Avalanche o Solana) han implementado sistemas de **sharding** y **subredes paralelas**, 
que permiten procesar miles de transacciones por segundo sin sacrificar descentralización.

Además, los modelos **edge computing** y **fog computing** se integran con blockchain para acercar el procesamiento de datos al punto de origen, 
reduciendo la latencia y mejorando la seguridad de dispositivos IoT, ciudades inteligentes y sistemas de trazabilidad industrial.`
    },
    {
      id: "tipos-arquitecturas",
      heading: "Tipos de arquitecturas distribuidas",
      text: `En la actualidad se pueden identificar distintos modelos de arquitectura digital, cada uno con un grado diferente de centralización:

1. **Arquitectura Monolítica (Centralizada):**  
   Todos los procesos y datos residen en un único servidor o entidad.  
   Es simple y eficiente, pero presenta un punto único de falla y dependencia total de la autoridad que la controla.

2. **Arquitectura Descentralizada:**  
   Existen varios servidores o entidades que gestionan partes del sistema, compartiendo responsabilidades.  
   No obstante, la confianza aún puede concentrarse en ciertos nodos principales.

3. **Arquitectura Distribuida:**  
   Todos los nodos poseen la misma copia de la información y pueden validar o rechazar transacciones.  
   El sistema se autorregula sin una jerarquía fija, favoreciendo la tolerancia a fallos y la independencia.

4. **Arquitectura Blockchain (Distribuida Inmutable):**  
   Es una evolución de la arquitectura distribuida clásica.  
   Cada bloque contiene un registro criptográfico irreversible, enlazado al bloque anterior mediante un hash.  
   Su diseño hace que la información sea inmutable y verificable públicamente, creando una base de datos de confianza compartida entre usuarios que no necesariamente se conocen entre sí.

5. **Arquitectura Híbrida (Blockchain + Cloud):**  
   Combina nodos distribuidos con servicios en la nube.  
   Se usa en entornos empresariales donde se requiere tanto transparencia como cumplimiento normativo.  
   Ejemplos modernos incluyen redes empresariales sobre **Hyperledger Fabric**, **Quorum**, o soluciones **Blockchain as a Service (BaaS)**.`
    },
    {
      id: "aplicaciones",
      heading: "Aplicaciones actuales de la arquitectura distribuida",
      text: `Las arquitecturas distribuidas son la base de una gran variedad de sistemas modernos:
      
- **Finanzas descentralizadas (DeFi):** intercambio de valor sin intermediarios.  
- **Cadenas de suministro inteligentes:** trazabilidad y autenticación de productos.  
- **Salud digital:** almacenamiento cifrado de historiales médicos.  
- **Educación y credenciales digitales:** validación descentralizada de títulos y certificados.  
- **Gobierno electrónico:** sistemas de votación y registros públicos transparentes.  
- **Internet de las cosas (IoT):** coordinación segura entre dispositivos interconectados.`
    },
    
  ]
};

export default BLOCKCHAIN_FUNDAMENTOS_CH07;
